// src/features/developer/components/Testing.tsx
import React, { useState } from 'react'
import { fetchData } from '@/utils/fetch'
import { fetchProductReportApi, ProductReportItem } from '@/features/services/sales/api/SalesPurchaseApi'
import { RangeDatePicker, RangeStrings } from '@shared/components/ui/RangeDatePicker'
import DailyLineGraph from '@shared/components/widgets/DailyLineGraph'

export default function Testing() {
  const [dateRange, setDateRange] = useState<RangeStrings | undefined>()
  const [compress, setCompress] = useState(false)

  const [outcomeResult, setOutcomeResult] = useState<any>(null)
  const [outcomeError,  setOutcomeError]  = useState<string | null>(null)

  const [productReport, setProductReport] = useState<ProductReportItem[] | null>(null)
  const [productError,  setProductError]  = useState<string | null>(null)

  // Helpers for computing start/end (defaults to today)
  const getStart = () =>
    dateRange?.from ?? `${new Date().toISOString().slice(0,10)}T00:00:00`
  const getEnd   = () =>
    dateRange?.to   ?? `${new Date().toISOString().slice(0,10)}T23:59:59`

  const handleFetchOutcome = async () => {
    setOutcomeError(null)
    setOutcomeResult(null)

    const payload: Record<string, any> = {
      id_service:  9,
      id_category: 4,
      date_range: { start: getStart(), end: getEnd() },
    }
    if (compress) {
      payload.compression = { unit: 'Hour', kind: 'Sum' }
    }

    try {
      const data = await fetchData(
        '/backend_services_rust/outcomes/get_outcome_run',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`,
          },
          body: JSON.stringify(payload),
        }
      )
      setOutcomeResult(data)
    } catch (err: any) {
      setOutcomeError(err.message)
    }
  }

  const handleFetchProductReport = async () => {
    setProductError(null)
    setProductReport(null)

    if (!dateRange?.from || !dateRange.to) {
      setProductError('Please select a start and end date first')
      return
    }

    try {
      const rpt = await fetchProductReportApi(
        0,
        dateRange.from,
        dateRange.to
      )
      setProductReport(rpt)
    } catch (err: any) {
      setProductError(err.message)
    }
  }

  return (
    <div className="p-4 rounded-md text-neutral-100 space-y-6">
      {/* ─── Controls ────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-4">
        <RangeDatePicker
          initialRange={dateRange}
          onRangeChange={setDateRange}
        />
        <label className="inline-flex items-center text-sm">
          <input
            type="checkbox"
            className="form-checkbox h-5 w-5 text-emerald-500"
            checked={compress}
            onChange={() => setCompress((c) => !c)}
          />
          <span className="ml-2 text-neutral-200">Compress by hour (sum)</span>
        </label>
      </div>

      {/* ─── Fetch Buttons ──────────────────────────────────────── */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={handleFetchOutcome}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-md"
        >
          Fetch Outcome
        </button>
        <button
          onClick={handleFetchProductReport}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-md"
        >
          Fetch Product Report
        </button>
      </div>

      {/* ─── Outcome Result ─────────────────────────────────────── */}
      {outcomeError && <p className="text-rose-400">Error: {outcomeError}</p>}
      {outcomeResult && (
        <pre className="border border-neutral-800 p-3 rounded overflow-auto text-sm">
          {JSON.stringify(outcomeResult, null, 2)}
        </pre>
      )}

      {/* ─── Product Report Chart ───────────────────────────────── */}
      {productError && <p className="text-rose-400">Error: {productError}</p>}
      {productReport && dateRange && (
        <div className="border border-neutral-800 p-4 rounded-md">
          <h3 className="text-neutral-200 mb-2">
            Quantity on {dateRange.from.split('T')[0]}
          </h3>
          <DailyLineGraph<ProductReportItem>
            data={productReport}
            xKey="timestamp"
            yKeys={['qty']}
            labels={['Qty']}
            day={new Date(dateRange.from)}
            width="100%"
            height={200}
            showGrid
            showLegend
          />
        </div>
      )}
    </div>
  )
}
